import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { translations } from '../lib/translations';
import { ArrowLeft, Bell, Check, FileText } from '@phosphor-icons/react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const lang = user?.preferred_language || 'ITA';
  const t = translations[lang];

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const goToContract = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    navigate(`/contract/${notification.contract_id}`);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-zinc-100 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <div className="flex items-center gap-3">
            <Bell size={24} weight="bold" className="text-zinc-700" />
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">Notifiche</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
            Caricamento...
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Bell size={48} className="text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">Nessuna notifica</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => goToContract(notification)}
                className={`w-full text-left bg-white border p-4 flex items-start gap-4 transition-colors hover:bg-zinc-50 ${
                  notification.read ? 'border-zinc-200' : 'border-green-500 bg-green-50'
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <div className={`w-10 h-10 flex items-center justify-center ${
                  notification.read ? 'bg-zinc-100' : 'bg-green-100'
                }`}>
                  {notification.type === 'contract_signed' ? (
                    <Check size={20} weight="bold" className={notification.read ? 'text-zinc-600' : 'text-green-600'} />
                  ) : (
                    <FileText size={20} weight="bold" className={notification.read ? 'text-zinc-600' : 'text-green-600'} />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${notification.read ? 'text-zinc-700' : 'text-zinc-900'}`}>
                    {notification.message}
                  </p>
                  <p className="text-sm text-zinc-500 mt-1">
                    {format(new Date(notification.created_at), 'dd/MM/yyyy HH:mm')}
                  </p>
                </div>
                {!notification.read && (
                  <span className="w-3 h-3 rounded-full bg-green-600 status-dot-completed" />
                )}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
